// Copyright (c) 2025 Digital Asset (Switzerland) GmbH and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useState } from 'react';
import { useErrorBoundary } from 'react-error-boundary';
import { Typography } from '@mui/material';
import { GridColDef, GridRenderCellParams, GridToolbarContainer } from '@mui/x-data-grid';
import { ProviderConfiguration } from '@daml.js/utility-registry-app-v0/lib/Utility/Registry/App/V0/Configuration/Provider';
import { AsyncButton } from '../../../components/Button/AsyncButton';
import { SyncButton } from '../../../components/Button/SyncButton';
import Loading from '../../../components/Loading';
import PartyDisplay from '../../../components/PartyDisplay';
import { QueryTable } from '../../../components/Table/QueryTable';
import { useParties } from '../../../contexts/PartiesContext';
import useArchiveProviderConfiguration from '../../../hooks/mutations/registry/configuration/useArchiveProviderConfiguration';
import useMutate from '../../../hooks/mutations/useMutate';
import useRegistryProviderConfiguration from '../../../hooks/queries/registry/configuration/useRegistryProviderConfiguration';
import useRegistryProviderConfigurations from '../../../hooks/queries/registry/configuration/useRegistryProviderConfigurations';
import useProviderService from '../../../hooks/queries/registry/onboarding/useProviderService';
import RequiredCredentials from '../../../modules/Credential/RequiredCredentials';
import TemplateContract from '../../../utils/TemplateContract';
import { SUC_ARC_PROVIDER_CONF, ERR_ARC_PROVIDER_CONF } from '../../../utils/strings';
import { CreateProviderConfigurationDialog } from './CreateProviderConfigurationDialog';
import { ModifyProviderConfigurationDialog } from './ModifyProviderConfigurationDialog';

type Row = TemplateContract<ProviderConfiguration>;

type Action = {
  name: string;
  action: (r: Row) => Promise<unknown>;
};

const createHeaders: (createActions: (r: Row) => Action[]) => GridColDef<Row>[] = (
  createActions,
) => {
  const renderActionCell = (params: GridRenderCellParams<Row>) => {
    const actions = createActions(params.row);
    return (
      <>
        {actions.map(({ name, action }) => (
          <AsyncButton key={name} sx={{ mr: 1 }} onClick={() => action(params.row)}>
            {name}
          </AsyncButton>
        ))}
      </>
    );
  };

  return [
    {
      field: 'operator',
      headerName: 'Operator',
      renderCell: (params) => <PartyDisplay party={params.row.payload.operator} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'provider',
      headerName: 'Provider',
      renderCell: (params) => <PartyDisplay party={params.row.payload.provider} />,
      flex: 150,
      filterable: false,
    },
    {
      field: 'holderRequirement',
      headerName: 'Holder Requirement',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.holderRequirements} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'registrarRequirement',
      headerName: 'Registrar Requirement',
      renderCell: (params) => (
        <RequiredCredentials credentialRequirements={params.row.payload.registrarRequirements} />
      ),
      flex: 250,
      filterable: false,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      sortable: false,
      renderCell: (params) => renderActionCell(params),
      flex: 150,
      filterable: false,
    },
  ];
};

const Toolbar = () => {
  const [createProviderConfigurationIsOpen, setCreateProviderConfigurationIsOpen] = useState(false);
  const configuration = useRegistryProviderConfiguration();

  const { isLoading } = configuration;

  return (
    <GridToolbarContainer>
      {isLoading ? null : (
        <>
          {createProviderConfigurationIsOpen && (
            <CreateProviderConfigurationDialog
              open={createProviderConfigurationIsOpen}
              onClose={() => setCreateProviderConfigurationIsOpen(false)}
            />
          )}
          <SyncButton
            onClick={() => setCreateProviderConfigurationIsOpen(true)}
            disabled={!!configuration.data}
          >
            Create provider configuration
          </SyncButton>
        </>
      )}
    </GridToolbarContainer>
  );
};

export const ProviderConfigurations = () => {
  const [modifyProviderConfiguration, setModifyProviderConfiguration] = useState<
    TemplateContract<ProviderConfiguration> | undefined
  >();

  const configurations = useRegistryProviderConfigurations();
  const providerService = useProviderService();
  const archiveProviderConfiguration = useArchiveProviderConfiguration();
  const { party } = useParties();
  const mutate = useMutate();
  const { showBoundary } = useErrorBoundary<Error>();

  const archive = useCallback(
    (row: Row) => {
      if (!providerService.data) {
        showBoundary(
          new Error(ERR_ARC_PROVIDER_CONF, { cause: 'Provider service is not available.' }),
        );
        return Promise.resolve();
      }
      const payload = {
        providerServiceCid: providerService.data.contractId,
        providerConfigurationCid: row.contractId,
      };
      return mutate(
        archiveProviderConfiguration,
        payload,
        SUC_ARC_PROVIDER_CONF,
        ERR_ARC_PROVIDER_CONF,
      );
    },
    [providerService, archiveProviderConfiguration, mutate, showBoundary],
  );

  const createActions = (r: Row) => {
    const actions: Action[] = [];
    if (r.payload.provider === party)
      actions.push(
        { name: 'Modify', action: () => Promise.resolve(setModifyProviderConfiguration(r)) },
        { name: 'Archive', action: archive },
      );
    return actions;
  };

  const isLoading =
    configurations.isLoading || providerService.isLoading || archiveProviderConfiguration.isLoading;
  if (isLoading) return <Loading />;

  const hasData = configurations.data && providerService.data;
  if (!hasData) return <Typography>No configurations</Typography>;

  return (
    <>
      {!!modifyProviderConfiguration && (
        <ModifyProviderConfigurationDialog
          open={!!modifyProviderConfiguration}
          onClose={() => setModifyProviderConfiguration(undefined)}
          providerConfiguration={modifyProviderConfiguration}
        />
      )}
      <QueryTable
        title="Provider Configurations"
        variant="h3"
        enableTableWrapper
        columns={createHeaders(createActions)}
        rowQuery={configurations}
        rowKey={(a) => a.contractId}
        toolbar={Toolbar}
      />
    </>
  );
};
